import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Add from '@material-ui/icons/Add';
import Info from '@material-ui/icons/Info';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Dayjs } from 'dayjs';
import { setDocumentsType } from '../../features/documents/actions';
import { addSnackbar } from '../../features/snackbar/actions';
import { reloadProject } from '../../features/project/actions';
import {
  DocumentTemplateType,
  IBidSetup,
  SecurityPermissionLevel,
} from '../../api-client/autogenerated';
import {
  getDisableProcoreIntegrationFeatures,
  getPhysicalLocationsState,
  getProjectState,
} from '../../features/project/selectors';
import { getCurrentSecurityGroup } from '../../features/security/selectors';
import { getProductPackageState } from '../../features/product-package/selectors';
import { getUserState } from '../../features/user/selectors';
import {
  canEditBidManagement,
  getBidTabulationUploadBlockedReason,
} from '../../scripts/permission-utils';
import {
  canViewBidding,
  canViewConstructionAndCloseout,
  canViewDesign,
  DOCUMENT_TYPES_CLOSE_OUT,
  DOCUMENT_TYPES_CONSTRUCTION,
  DOCUMENT_TYPES_DESIGN,
  docTypesNoCreateOrEdit,
  documentTypeToUrl,
  getPermissionForDocumentType,
  getUserFriendlyDocumentTemplateNameSingular,
  parseDate,
} from '../../scripts/utils';
import {
  BiddingUploadFile,
  UploadHandlers,
  uploadAddendum,
  uploadBidAdvertisement,
  uploadCompletedBidTabulation,
  uploadInformationalItem,
} from '../../models/api/bidding-uploads';
import { getBidSetupById, patchBidSetup } from '../../models/api/project-bid-setups';
import { useAppUtilsContext } from '../AppUtilsContext';
import AddDocumentDialog from '../dialogs/AddDocumentDialog';
import AddPackageDialog from '../dialogs/AddPackageDialog';
import SelectSubmittalsDialog, { SubmittalPackageSelection } from './SelectSubmittalsDialog';
import FileUploadDialog from '../dialogs/FileUploadDialog';
import ModifyBidDateDialog from '../dialogs/ModifyBidDateDialog';
import PublishFileDialog from '../dialogs/PublishFileDialog';
import CreatePunchListDialog from '../punch-lists/CreatePunchListDialog';
import { TooltipIfDisabledComponent } from '../custom-components/CustomButtons';
import DesignIcon from '../icons/Design-icon';
import BiddingIcon from '../icons/Bidding-icon';
import ConstructionIcon from '../icons/Construction-icon';
import CloseOutIcon from '../icons/CloseOut-icon';
import { AssignmentTurnedInOutlined } from '@material-ui/icons';
import { canEditProjectLifecycleForms } from '../project-lifecycle/utils';
import { STORYTELLING_NAV_OPTIONS } from '../project-lifecycle/data';

const useStyles = makeStyles({
  root: {
    background: '#FFFFFF',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    height: '100%',
  },
  titleStyle: {
    background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
    borderRadius: '4px 4px 0px 0px',
  },
  accordionSummary: {
    minHeight: 44,
    '&$expanded': { minHeight: 44 },
  },
  expanded: {},
  phaseTitle: {
    fontWeight: 500,
    fontSize: 16,
  },
  typeButton: {
    justifyContent: 'flex-start',
    textAlign: 'left',
    textTransform: 'none',
    width: '100%',
  },
  /** Wraps the button so the tooltip has something to listen on even when the button is dead. */
  buttonRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  /**
   * Inside the button, in the button's own colour: it marks the button as the thing that has an
   * explanation, rather than being a separate grey control sitting next to it.
   */
  hintIcon: {
    color: 'inherit',
    fontSize: 18,
    flexShrink: 0,
  },
});

/**
 * Types whose creation does not start in AddDocumentDialog. Sending these to their log page keeps
 * the dashboard from becoming a second place where each rule has to be restated -- the As-built and
 * closeout submittal upload workflows, and the "only one certificate of substantial completion"
 * check, all live on the log page toolbar. Submittals are handled here instead, see
 * handleTypeClick.
 */
const DOC_TYPES_CREATED_ON_LOG_PAGE = [
  DocumentTemplateType.CloseoutSubmittals,
  DocumentTemplateType.AsBuilt,
  DocumentTemplateType.CertificateOfSubstantialCompletion,
];

/**
 * Packages are made by gathering documents that already exist, not created from nothing, so they do
 * not belong in a list of things to create. Submittal packages are reached by picking several
 * submittals; see handleTypeClick.
 */
const DOC_TYPES_NOT_CREATED_DIRECTLY = [DocumentTemplateType.SubmittalPackages];

/** The bidding uploads the card can carry out without leaving the dashboard. */
type BiddingUpload = 'addendum' | 'informationalItem' | 'bidAdvertisement' | 'bidTabulation';

const BIDDING_UPLOAD_LABELS: Record<BiddingUpload, string> = {
  addendum: 'Addendum',
  informationalItem: 'Informational Item',
  bidAdvertisement: 'Bid Advertisement',
  bidTabulation: 'Completed Bid Tabulation',
};

/** The two uploads that create a document the user has to name, and may span several files. */
const BIDDING_UPLOADS_WITH_TITLE: BiddingUpload[] = ['addendum', 'informationalItem'];

type BiddingAction = {
  label: string;
  disabledReason?: string;
  onClick: () => void;
};

const PHASES: { title: string; types: DocumentTemplateType[]; icon: JSX.Element }[] = [
  {
    title: 'Design',
    types: DOCUMENT_TYPES_DESIGN,
    icon: <DesignIcon fill="#0247B9" width={22} height={22} />,
  },
  {
    title: 'Construction',
    types: DOCUMENT_TYPES_CONSTRUCTION,
    icon: <ConstructionIcon fill="#0247B9" width={22} height={22} />,
  },
  {
    title: 'Close Out',
    types: DOCUMENT_TYPES_CLOSE_OUT,
    icon: <CloseOutIcon fill="#0247B9" width={22} height={22} />,
  },
];

export default function DashboardCreateDocument() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const project = useSelector(getProjectState);
  const disableProcoreIntegrationFeatures = useSelector(getDisableProcoreIntegrationFeatures);
  const user = useSelector(getUserState);
  const security = useSelector(getCurrentSecurityGroup);
  const productPackage = useSelector(getProductPackageState);
  const locations = useSelector(getPhysicalLocationsState);

  const {
    documentTemplates: { documentTemplateNameToId },
  } = useAppUtilsContext();

  // The popover anchors to the button that was actually clicked, so a single shared ref will not do.
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [createType, setCreateType] = useState<DocumentTemplateType>();
  // Set only where the caller has already settled the question; undefined lets AddDocumentDialog
  // decide from permission, which is right for every other button on this card.
  const [createAsPlaceholder, setCreateAsPlaceholder] = useState<boolean>();
  const [punchListDialogOpen, setPunchListDialogOpen] = useState(false);
  // Building a submittal package runs in two steps: gather the submittals, then name the package.
  // The submittals picked in the first step are held here for the second.
  const [packageSubmittalsOpen, setPackageSubmittalsOpen] = useState(false);
  const [packageSelections, setPackageSelections] = useState<SubmittalPackageSelection[]>();

  // Bidding uploads run here rather than on Bid Management, so the card carries the same pieces that
  // page does: which upload is open, the files staged for it, and the progress shown while it runs.
  const [biddingUpload, setBiddingUpload] = useState<BiddingUpload>();
  const [pendingFiles, setPendingFiles] = useState<BiddingUploadFile[]>([]);
  const [bidDateDialogOpen, setBidDateDialogOpen] = useState(false);
  // The project in redux carries only bidSetupId, so the bid setup itself is fetched here. It is
  // what decides whether the tabulation may be uploaded, and what the bid date dialog shows.
  const [bidSetup, setBidSetup] = useState<IBidSetup>();
  // null means the user did not change the bid date; a value is a change waiting on the upload.
  const [pendingBidDate, setPendingBidDate] = useState<Dayjs | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string>();
  const [uploadProgress, setUploadProgress] = useState<number>();
  const [publishFileDialogOpen, setPublishFileDialogOpen] = useState(false);
  const [publishTabulation, setPublishTabulation] = useState(false);

  const canViewPhase = (title: string) => {
    switch (title) {
      case 'Design':
        return canViewDesign(productPackage, security, user, project);
      case 'Bidding':
        return canViewBidding(productPackage);
      default:
        return canViewConstructionAndCloseout(productPackage);
    }
  };

  const visiblePhases = useMemo(() => {
    return PHASES.map((phase) => ({
      ...phase,
      types: canViewPhase(phase.title)
        ? phase.types.filter(
            (type) =>
              !docTypesNoCreateOrEdit.includes(type) &&
              !DOC_TYPES_NOT_CREATED_DIRECTLY.includes(type) &&
              getPermissionForDocumentType(
                type,
                security,
                documentTemplateNameToId,
                user,
                project,
              ) >= SecurityPermissionLevel.NUMBER_2,
          )
        : [],
    })).filter((phase) => phase.types.length > 0);
  }, [security, documentTemplateNameToId, user, project, productPackage]);

  // Bid Management is where every bidding document is published from, so the section is offered to
  // whoever may act there -- the same gate that enables its own upload buttons.
  const canManageBidding =
    canViewBidding(productPackage) && canEditBidManagement(user, security, project);
  const hasCompletedBidSetup = !!project?.bidSetupId;

  // Storytelling is offered to whoever may fill its forms in -- the same gate the forms' own Save
  // buttons use -- and only where the subscriber has the module at all.
  const canCreateStorytelling = canEditProjectLifecycleForms(
    project,
    user,
    project?.subscriber,
    security,
  );

  const storytellingActions: BiddingAction[] = useMemo(() => {
    if (!canCreateStorytelling || !project) return [];
    return STORYTELLING_NAV_OPTIONS.map((option) => ({
      label: option.label,
      onClick: () => history.push(`/main/projects/${project.id}/${option.path}`),
    }));
  }, [canCreateStorytelling, project?.id]);

  // Only fetched for the users who are shown the Bidding section, and only once the project has a
  // bid setup to fetch.
  const bidSetupId = canManageBidding ? project?.bidSetupId : undefined;
  useEffect(() => {
    if (!bidSetupId) {
      setBidSetup(undefined);
      return undefined;
    }
    let stale = false;
    getBidSetupById(bidSetupId).then((fetched) => {
      if (!stale) setBidSetup(fetched);
    });
    return () => {
      stale = true;
    };
  }, [bidSetupId]);

  // Bidding sits between Design and Construction, as it does everywhere else in the product.
  const orderedSections = useMemo(() => {
    const sections: { title: string; types: DocumentTemplateType[]; icon: JSX.Element }[] = [
      ...visiblePhases,
    ];
    if (!canManageBidding) return sections;
    const biddingSection = {
      title: 'Bidding',
      types: [] as DocumentTemplateType[],
      icon: <BiddingIcon fill="#0247B9" width={22} height={22} />,
    };
    const designIndex = sections.findIndex((section) => section.title === 'Design');
    sections.splice(designIndex + 1, 0, biddingSection);
    return sections;
  }, [visiblePhases, canManageBidding]);

  // Storytelling closes the list, as it does in the navigation drawer.
  const sectionsToRender = useMemo(() => {
    if (storytellingActions.length === 0) return orderedSections;
    return [
      ...orderedSections,
      {
        title: 'Storytelling',
        types: [] as DocumentTemplateType[],
        icon: <AssignmentTurnedInOutlined htmlColor="#0247B9" style={{ fontSize: 22 }} />,
      },
    ];
  }, [orderedSections, storytellingActions.length]);

  const goToLogPage = (type: DocumentTemplateType) =>
    history.push(`/main/projects/${project!.id}/documents/${documentTypeToUrl[type]}`);

  const goToBidManagement = () => history.push(`/main/projects/${project!.id}/bid-mgmt`);

  const startBiddingUpload = (upload: BiddingUpload) => {
    setPendingFiles([]);
    setPendingBidDate(null);
    setUploadProgress(undefined);
    setPublishTabulation(false);
    // Issuing an addendum is the moment to revisit the bid date, exactly as it is on Bid Setup. Any
    // new date is only held here -- see finishBiddingUpload for why it is not sent yet.
    if (upload === 'addendum') setBidDateDialogOpen(true);
    else setBiddingUpload(upload);
  };

  const cancelBiddingUpload = () => {
    setBiddingUpload(undefined);
    setPendingFiles([]);
    // Nothing was sent, so an abandoned upload leaves the bid date exactly as it was.
    setPendingBidDate(null);
    setUploadMessage(undefined);
    setUploadProgress(undefined);
    setPublishFileDialogOpen(false);
    setPublishTabulation(false);
  };

  const uploadHandlers: UploadHandlers = {
    onProgress: (event) => setUploadProgress(Math.round((event.loaded / event.total) * 100)),
    onFileStart: (fileName, index, total) => {
      setUploadProgress(0);
      setUploadMessage(total > 1 ? `Uploading ${fileName} (${index}/${total})` : 'Uploading file');
    },
    // FileUploadDialog swaps the progress bar for a spinner once the message stops saying
    // "uploading", which is what this wording is for -- there is no progress left to show.
    onVerifying: () => setUploadMessage('Processing files'),
  };

  const finishBiddingUpload = async (upload: BiddingUpload, title?: string) => {
    if (!project) return;
    setUploadMessage('Uploading file');
    setUploadProgress(0);
    try {
      if (upload === 'addendum') {
        // The date change rides with the addendum rather than with the dialog that collected it: a
        // user who picks a new date and then abandons the upload should not have moved the bid date.
        // It still lands first, as it does on Bid Setup, where the bid setup is patched before the
        // addenda are inserted.
        if (pendingBidDate) {
          setUploadMessage('Updating bid date');
          await patchBidSetup(project.id, { dueDate: pendingBidDate.toISOString() });
          if (project.bidSetupId) setBidSetup(await getBidSetupById(project.bidSetupId));
        }
        await uploadAddendum(
          {
            projectId: project.id,
            creatorUserId: user.id,
            title: title!,
            files: pendingFiles,
          },
          uploadHandlers,
        );
      } else if (upload === 'informationalItem') {
        await uploadInformationalItem(
          {
            projectId: project.id,
            creatorUserId: user.id,
            title: title!,
            files: pendingFiles,
          },
          uploadHandlers,
        );
      } else if (upload === 'bidAdvertisement') {
        await uploadBidAdvertisement(
          { projectId: project.id, creatorUserId: user.id, file: pendingFiles[0] },
          uploadHandlers,
        );
      } else {
        await uploadCompletedBidTabulation(
          { projectId: project.id, file: pendingFiles[0], isPublic: publishTabulation },
          uploadHandlers,
        );
      }
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: `${BIDDING_UPLOAD_LABELS[upload]} published`,
          severity: 'success',
        }),
      );
      // The tabulation lands on the project itself, and the bid date may have moved, so the card
      // needs the project again to show the right state.
      dispatch(reloadProject());
    } catch (error: any) {
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: error?.message || 'Upload failed',
          severity: 'error',
        }),
      );
    } finally {
      cancelBiddingUpload();
    }
  };

  const addPendingFiles = (files: BiddingUploadFile[]) => {
    setPendingFiles((previous) => [...previous, ...files]);
    // Whether the completed tabulation goes out on the public bidding portal is asked as soon as the
    // file is chosen, the same as on Bid Management.
    if (biddingUpload === 'bidTabulation') setPublishFileDialogOpen(true);
  };

  const removePendingFile = (file: BiddingUploadFile) =>
    setPendingFiles((previous) => previous.filter((pending) => pending !== file));

  const biddingActions: BiddingAction[] = hasCompletedBidSetup
    ? [
        { label: 'Addendum', onClick: () => startBiddingUpload('addendum') },
        { label: 'Informational Item', onClick: () => startBiddingUpload('informationalItem') },
        { label: 'Bid Advertisement', onClick: () => startBiddingUpload('bidAdvertisement') },
        {
          label: 'Completed Bid Tabulation',
          disabledReason: getBidTabulationUploadBlockedReason(user, security, project, bidSetup),
          onClick: () => startBiddingUpload('bidTabulation'),
        },
      ]
    : [{ label: 'Bid Setup', onClick: goToBidManagement }];

  const submittalPermission = getPermissionForDocumentType(
    DocumentTemplateType.Submittals,
    security,
    documentTemplateNameToId,
    user,
    project,
  );
  // A reviewer creates a submittal the contractor has yet to fill in -- a placeholder. Anyone with
  // only create permission is instead working from submittals that already exist.
  const createsSubmittalPlaceholder = submittalPermission >= SecurityPermissionLevel.NUMBER_3;

  /**
   * A package is gathered from submittals that already exist rather than created from nothing, so
   * it is offered beside them instead of through the list of types. Which package the button makes
   * is the split the log page toolbar draws off the same submittal permission: CREATE uploads the
   * package itself (AddPackageDialog then requires the file), REVIEW and above create the
   * placeholder a contractor fills in later.
   */
  const canCreateSubmittalPackage = submittalPermission >= SecurityPermissionLevel.NUMBER_2;
  const submittalPackageLabel = createsSubmittalPlaceholder
    ? 'Create Submittal Package Placeholder'
    : 'Upload Submittal Package';

  /**
   * Procore owns the submittals and RFIs of a synced project, so the card cannot start one: the
   * same rule the submittal log's toolbar and the document display already apply, said here too
   * rather than letting the user open a dialog that has nothing to offer.
   */
  const PROCORE_MANAGED_TYPES = [
    DocumentTemplateType.Submittals,
    DocumentTemplateType.RequestsForInformation,
  ];
  const PROCORE_TOOLTIP = 'Currently Syncing with Procore';

  const openSubmittalPackagePicker = (event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
    // AddPackageDialog reads the log page's current type out of redux to decide which kind of
    // package to make, and where to send the user afterwards. Set it before opening the picker.
    dispatch(setDocumentsType(DocumentTemplateType.Submittals));
    setPackageSelections(undefined);
    setPackageSubmittalsOpen(true);
  };

  /**
   * One creation button, with whatever it needs to explain itself.
   *
   * The hint sits inside the button in the button's own colour and the whole button is the hover
   * target, so the explanation belongs to the button rather than to a small grey icon beside it
   * that has to be found first. A button something has made dead says that instead -- the two
   * never both apply, and a disabled button fires no pointer events of its own, which is why the
   * tooltip listens on the wrapper either way.
   */
  const renderCreateButton = (options: {
    label: string;
    hint?: string;
    disabled?: boolean;
    disabledReason?: string;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
  }) => {
    const { label, hint, disabled, disabledReason, onClick } = options;
    const title = (disabled ? disabledReason : hint) ?? '';
    const button = (
      <Button
        variant="outlined"
        color="primary"
        size="small"
        disabled={disabled}
        startIcon={<Add />}
        endIcon={hint ? <Info className={classes.hintIcon} /> : undefined}
        className={classes.typeButton}
        onClick={onClick}
      >
        {label}
      </Button>
    );
    if (!title) return <div className={classes.buttonRow}>{button}</div>;
    return (
      <Tooltip arrow placement="top" title={title}>
        <div className={classes.buttonRow}>{button}</div>
      </Tooltip>
    );
  };

  const handleTypeClick = (type: DocumentTemplateType, event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
    // AddDocumentDialog and AddPackageDialog both read the log page's current type out of redux --
    // getDocumentTypePermission, which decides whether a submittal is a placeholder, is keyed on it.
    // Set it before opening either, or they act on whatever type was last browsed.
    dispatch(setDocumentsType(type));
    if (type === DocumentTemplateType.Submittals) {
      // A reviewer creates the placeholder for a contractor to fill in. Everyone else is uploading
      // against a submittal that already exists, or one they are about to create -- the dialog
      // asks which, so both land in the same place.
      setCreateAsPlaceholder(createsSubmittalPlaceholder);
      setCreateType(type);
    } else if (type === DocumentTemplateType.PunchList) {
      setPunchListDialogOpen(true);
    } else if (type === DocumentTemplateType.FieldReports) {
      history.push(
        `/main/projects/${project!.id}/documents/${
          documentTypeToUrl[DocumentTemplateType.FieldReports]
        }/create`,
      );
    } else if (DOC_TYPES_CREATED_ON_LOG_PAGE.includes(type)) {
      goToLogPage(type);
    } else {
      setCreateType(type);
    }
  };

  // Punch list items are placed in a location, so there is nothing to create until the project has
  // been through punch list setup. Same rule and wording as the log page toolbar.
  const punchListDisabled = locations.length === 0;

  return (
    <Card className={classes.root}>
      <CardContent className={classes.titleStyle}>
        <Grid container style={{ justifyContent: 'space-between' }}>
          <Grid item>
            <h2 className="h2" style={{ textAlign: 'left', color: '#FFFFFF', margin: 0 }}>
              Create Anything
            </h2>
          </Grid>
        </Grid>
      </CardContent>
      <CardContent>
        {visiblePhases.length === 0 && !canManageBidding && storytellingActions.length === 0 ? (
          <Typography align="center" style={{ fontSize: 16, marginTop: 8 }}>
            You do not have permission to create documents on this project.
          </Typography>
        ) : (
          sectionsToRender.map((phase) =>
            // Both of these sections list fixed actions rather than document types, so they share
            // the same button layout.
            phase.title === 'Bidding' || phase.title === 'Storytelling' ? (
              <Accordion key={phase.title} elevation={0} square>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  classes={{ root: classes.accordionSummary, expanded: classes.expanded }}
                >
                  {phase.icon}
                  <Typography className={classes.phaseTitle} style={{ marginLeft: 8 }}>
                    {phase.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ padding: '0px 8px 8px 8px' }}>
                  <Grid container spacing={1}>
                    {(phase.title === 'Storytelling' ? storytellingActions : biddingActions).map(
                      (action) => (
                        <Grid item xs={12} sm={6} key={action.label}>
                          <TooltipIfDisabledComponent
                            disabled={!!action.disabledReason}
                            title={action.disabledReason ?? ''}
                          >
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              disabled={!!action.disabledReason}
                              startIcon={<Add />}
                              className={classes.typeButton}
                              onClick={action.onClick}
                            >
                              {action.label}
                            </Button>
                          </TooltipIfDisabledComponent>
                        </Grid>
                      ),
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Accordion key={phase.title} elevation={0} square>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  classes={{ root: classes.accordionSummary, expanded: classes.expanded }}
                >
                  {phase.icon}
                  <Typography className={classes.phaseTitle} style={{ marginLeft: 8 }}>
                    {phase.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ padding: '0px 8px 8px 8px' }}>
                  <Grid container spacing={1}>
                    {phase.types.map((type) => {
                      const procoreManaged =
                        disableProcoreIntegrationFeatures && PROCORE_MANAGED_TYPES.includes(type);
                      const disabled =
                        procoreManaged ||
                        (type === DocumentTemplateType.PunchList && punchListDisabled);
                      return (
                        <React.Fragment key={type}>
                          <Grid item xs={12} sm={6}>
                            {renderCreateButton({
                              label:
                                type === DocumentTemplateType.Submittals
                                  ? createsSubmittalPlaceholder
                                    ? 'Submittal Placeholder'
                                    : 'Upload Submittals'
                                  : getUserFriendlyDocumentTemplateNameSingular(type),
                              hint:
                                type === DocumentTemplateType.Submittals
                                  ? 'For a single Submittal'
                                  : undefined,
                              disabled,
                              disabledReason: procoreManaged
                                ? PROCORE_TOOLTIP
                                : 'Complete the setup process before Punch List items can created.',
                              onClick: (event) => handleTypeClick(type, event),
                            })}
                          </Grid>
                          {/* A package is gathered from submittals, so it follows them rather than
                              sitting at the end of the section. */}
                          {type === DocumentTemplateType.Submittals && canCreateSubmittalPackage ? (
                            <Grid item xs={12} sm={6}>
                              {renderCreateButton({
                                label: submittalPackageLabel,
                                hint: 'For submitting multiple Submittals at one time',
                                disabled: disableProcoreIntegrationFeatures,
                                disabledReason: PROCORE_TOOLTIP,
                                onClick: openSubmittalPackagePicker,
                              })}
                            </Grid>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ),
          )
        )}
      </CardContent>

      {createType ? (
        <AddDocumentDialog
          dialogOpen={!!createType}
          handleClose={() => {
            setCreateType(undefined);
            setCreateAsPlaceholder(undefined);
          }}
          anchor={anchor}
          dashboardDocumentType={createType}
          createAsPlaceholder={createAsPlaceholder}
          isSubmittalUpload={
            createType === DocumentTemplateType.Submittals && !createsSubmittalPlaceholder
          }
        />
      ) : null}
      {/* Step one of a package: which submittals it gathers. */}
      {packageSubmittalsOpen ? (
        <SelectSubmittalsDialog
          open
          projectId={project!.id}
          documentType={DocumentTemplateType.Submittals}
          isPlaceholder={createsSubmittalPlaceholder}
          handleClose={() => setPackageSubmittalsOpen(false)}
          onCreatePackage={(selections) => {
            setPackageSubmittalsOpen(false);
            setPackageSelections(selections);
          }}
        />
      ) : null}
      {/* Step two: naming it, and -- unless it is a placeholder -- the file it carries. */}
      {packageSelections ? (
        <AddPackageDialog
          dialogOpen
          handleClose={() => setPackageSelections(undefined)}
          anchor={anchor}
          selected={
            packageSelections.map((selection) => selection.documentId).filter(Boolean) as string[]
          }
          submittalsToCreate={
            packageSelections.map((selection) => selection.create).filter(Boolean) as NonNullable<
              SubmittalPackageSelection['create']
            >[]
          }
          isPlaceholder={createsSubmittalPlaceholder}
          selectedSummaries={packageSelections.map((selection) => ({
            id: selection.key,
            label: selection.label,
          }))}
        />
      ) : null}
      <CreatePunchListDialog
        open={punchListDialogOpen}
        handleClose={() => setPunchListDialogOpen(false)}
        anchor={anchor}
      />
      {/* Both bidding dialogs are mounted only while in use so they start clean each time -- the
          bid date dialog remembers whether the user chose to change the date, and the upload dialog
          remembers the title that was typed. */}
      {bidDateDialogOpen ? (
        <ModifyBidDateDialog
          open
          handleClose={() => {
            setBidDateDialogOpen(false);
            setBiddingUpload('addendum');
          }}
          bidDate={bidSetup?.dueDate ? parseDate(bidSetup.dueDate) : null}
          minDate={bidSetup?.preDueDate ? parseDate(bidSetup.preDueDate) : undefined}
          setBidDate={setPendingBidDate}
        />
      ) : null}
      {biddingUpload ? (
        <FileUploadDialog
          open
          title={`Upload ${BIDDING_UPLOAD_LABELS[biddingUpload]}`}
          handleClose={cancelBiddingUpload}
          buttonType="submit"
          handleSubmit={(title) => finishBiddingUpload(biddingUpload, title)}
          newFiles={pendingFiles}
          addFile={(file) => addPendingFiles([file as BiddingUploadFile])}
          addFiles={(files) => addPendingFiles(files as BiddingUploadFile[])}
          removeFile={(file) => removePendingFile(file as BiddingUploadFile)}
          canSubmit={pendingFiles.length > 0}
          allowMultiple={BIDDING_UPLOADS_WITH_TITLE.includes(biddingUpload)}
          requireDocumentTitle={BIDDING_UPLOADS_WITH_TITLE.includes(biddingUpload)}
          disableComments
          uploadMessage={uploadMessage}
          uploadProgress={uploadProgress}
        />
      ) : null}
      <PublishFileDialog
        open={publishFileDialogOpen}
        submit={(willPublish) => {
          setPublishTabulation(willPublish);
          setPublishFileDialogOpen(false);
        }}
      />
    </Card>
  );
}
